/**
 * Sentry client wrapper (NB-F9). Lazy-initializes @sentry/nextjs only
 * when `NEXT_PUBLIC_SENTRY_DSN` is set. Sentry does NOT require user
 * consent to initialize because error reports are operational data
 * (not analytics) per Sentry's GDPR posture. See Sentry's data
 * processing addendum. However we honor `Do-Not-Track` / GPC by
 * skipping init when the user has explicitly opted out of all data
 * processing via the cookie banner (`essential` consent level).
 *
 * Once initialized, the wrapper exposes `captureException(err, ctx)`
 * and `captureMessage(msg, ctx, level)` that delegate to the SDK.
 * `observability.ts` shim auto-detects and forwards to these.
 *
 * Source-map upload + release tagging requires `withSentryConfig`
 * wrapped around `next.config.js` and a `SENTRY_AUTH_TOKEN` in CI.
 * That hookup is a follow-up. This scaffolding is the runtime
 * client init only.
 */
'use client'

import { readConsent } from '@/components/CookieBanner'

let sentryClient: typeof import('@sentry/nextjs') | null = null
let initAttempted = false

function getDsn(): string | null {
  if (typeof process === 'undefined') return null
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN?.trim()
  return dsn && dsn.length > 0 ? dsn : null
}

function getEnvironment(): string {
  if (typeof process === 'undefined') return 'production'
  return process.env.NEXT_PUBLIC_SENTRY_ENV?.trim() || process.env.NODE_ENV || 'production'
}

function getRelease(): string | undefined {
  if (typeof process === 'undefined') return undefined
  return process.env.NEXT_PUBLIC_SENTRY_RELEASE?.trim() || undefined
}

export async function init(): Promise<void> {
  if (initAttempted) return
  initAttempted = true
  if (typeof window === 'undefined') return
  const dsn = getDsn()
  if (!dsn) return
  const consent = readConsent()
  // Respect explicit opt-out (essential-only consent). Error reports
  // serve operational reliability so we permit them on 'all',
  // 'analytics', AND null (no decision yet), but NOT 'essential'.
  if (consent === 'essential') return
  try {
    const mod = await import('@sentry/nextjs')
    mod.init({
      dsn,
      environment: getEnvironment(),
      release: getRelease(),
      // Performance monitoring sampling. 10% lets us catch tail latency
      // without burning the Sentry transaction quota on every navigation.
      tracesSampleRate: 0.1,
      // Capture replays on errors only (not all sessions).
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // Don't capture exceptions that originate inside our own observability
      // pipeline. Guards against feedback loops if Sentry itself misbehaves.
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
      ],
    })
    sentryClient = mod
  } catch {
    // SDK load failed (CSP block, offline at boot). Silent: observability
    // must never surface its own errors.
  }
}

export function captureException(err: unknown, context: Record<string, unknown> = {}): void {
  if (!sentryClient) return
  try {
    sentryClient.captureException(err, { extra: context })
  } catch {
    // No-op
  }
}

export function captureMessage(
  message: string,
  context: Record<string, unknown> = {},
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
): void {
  if (!sentryClient) return
  try {
    sentryClient.captureMessage(message, { level, extra: context })
  } catch {
    // No-op
  }
}

export function setUser(user: { id: string | number; email?: string } | null): void {
  if (!sentryClient) return
  try {
    if (user === null) {
      sentryClient.setUser(null)
    } else {
      sentryClient.setUser({ id: String(user.id), email: user.email })
    }
  } catch {
    // No-op
  }
}
